import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { VelvetCardLink } from "../../../../modules/content/components/VelvetCardLink/VelvetCardLink";
import { VelvetCommonContent } from "../../../../modules/content/components/VelvetCommonContent/VelvetCommonContent";
import { VelvetRichText } from "../../../../modules/content/components/VelvetRichText/VelvetRichText";
import { VelvetSection } from "../../../../modules/content/components/VelvetSection/VelvetSection";
import type { IIconContentGridBlockProps } from "./IconContentGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

export const IconContentGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: IIconContentGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <VelvetSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-16 md:gap-24">
        <VelvetCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div
          className={cn(
            "grid grid-cols-1 border-t border-parchment/50 sm:grid-cols-2",
            columns[columnCount || 3],
          )}
        >
          {items.map((item, index) => (
            <VelvetCardLink
              className={cn(
                "grid min-h-72 grid-cols-[4rem_1fr] content-start gap-x-5 border-b border-parchment/20 bg-transparent py-8 sm:px-6 sm:first:pl-0 sm:last:pr-0 md:min-h-80 md:grid-cols-[5rem_1fr] md:py-10",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <span className="inline-flex size-14 items-center justify-center border border-thread text-thread transition-colors group-hover:bg-thread group-hover:text-ink md:size-16">
                <Icon className="size-6" name={item.icon} />
              </span>
              <h3 className="pt-2 font-velvet-display text-4xl font-black uppercase leading-[0.9] tracking-[-0.035em] text-foreground">
                {item.heading}
              </h3>
              {item.description ? (
                <VelvetRichText
                  className="col-start-2 mt-5 text-base text-smoke md:text-lg"
                  content={item.description}
                />
              ) : null}
            </VelvetCardLink>
          ))}
        </div>
      </div>
    </VelvetSection>
  );
};

IconContentGridBlock.displayName = "Block::IconContentGridBlock";
